/**
 * Component Library Demo Page
 *
 * Comprehensive demo of all standard components.
 * Serves as documentation and visual testing.
 */

import React, { useState } from 'react';
import {
  StandardButton,
  StandardModal,
  StandardDataTable,
  StandardInput,
  StandardSelect,
  StandardCheckbox,
  StandardTextarea,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState,
  StandardSearchBar,
  StandardPagination,
  StandardBreadcrumbs,
  StandardCard,
  useToast,
  type TableColumn
} from '../components/standard';
import {
  Save,
  Trash2,
  Download,
  Inbox,
  User
} from 'lucide-react';

export const ComponentLibraryDemo: React.FC = () => {
  const { success, error, warning, info } = useToast();

  // Component state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [checkboxWithError, setCheckboxWithError] = useState(false);
  const [textareaValue, setTextareaValue] = useState('');
  const [textareaWithCount, setTextareaWithCount] = useState('');

  // Demo data
  const demoData = [
    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'Active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'Inactive' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', status: 'Active' }
  ];

  const tableColumns: TableColumn<typeof demoData[0]>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Active'
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
        }`}>
          {value}
        </span>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Standard Component Library
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Professional, reusable components for all applications
          </p>
        </div>

        {/* Breadcrumbs Demo */}
        <StandardCard>
          <StandardCard.Header title="Breadcrumbs" />
          <StandardCard.Body>
            <StandardBreadcrumbs
              items={[
                { label: 'Home', href: '#' },
                { label: 'Components', href: '#' },
                { label: 'Demo' }
              ]}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Buttons Demo */}
        <StandardCard>
          <StandardCard.Header title="Buttons" subtitle="7 variants × 5 sizes" />
          <StandardCard.Body className="space-y-4">
            {/* Variants */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Variants
              </h4>
              <div className="flex flex-wrap gap-2">
                <StandardButton variant="primary">Primary</StandardButton>
                <StandardButton variant="secondary">Secondary</StandardButton>
                <StandardButton variant="success">Success</StandardButton>
                <StandardButton variant="danger">Danger</StandardButton>
                <StandardButton variant="warning">Warning</StandardButton>
                <StandardButton variant="ghost">Ghost</StandardButton>
                <StandardButton variant="link">Link</StandardButton>
              </div>
            </div>

            {/* Sizes */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sizes
              </h4>
              <div className="flex flex-wrap items-center gap-2">
                <StandardButton size="xs">Extra Small</StandardButton>
                <StandardButton size="sm">Small</StandardButton>
                <StandardButton size="md">Medium</StandardButton>
                <StandardButton size="lg">Large</StandardButton>
                <StandardButton size="xl">Extra Large</StandardButton>
              </div>
            </div>

            {/* With Icons */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                With Icons
              </h4>
              <div className="flex flex-wrap gap-2">
                <StandardButton leftIcon={<Save size={16} />}>Save</StandardButton>
                <StandardButton variant="danger" leftIcon={<Trash2 size={16} />}>Delete</StandardButton>
                <StandardButton variant="secondary" rightIcon={<Download size={16} />}>Download</StandardButton>
              </div>
            </div>

            {/* Loading & Disabled */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                States
              </h4>
              <div className="flex flex-wrap gap-2">
                <StandardButton isLoading>Loading</StandardButton>
                <StandardButton disabled>Disabled</StandardButton>
                <StandardButton isFullWidth>Full Width</StandardButton>
              </div>
            </div>
          </StandardCard.Body>
        </StandardCard>

        {/* Modal Demo */}
        <StandardCard>
          <StandardCard.Header title="Modal" />
          <StandardCard.Body>
            <StandardButton onClick={() => setIsModalOpen(true)}>
              Open Modal
            </StandardButton>

            <StandardModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              title="Example Modal"
              size="md"
            >
              <StandardModal.Body>
                <p className="text-gray-600 dark:text-gray-400">
                  This is a demo modal. Click outside, press ESC, or click the close button to dismiss.
                </p>
              </StandardModal.Body>
              <StandardModal.Footer>
                <StandardButton variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </StandardButton>
                <StandardButton variant="primary" onClick={() => {
                  success('Success!', 'Modal action completed');
                  setIsModalOpen(false);
                }}>
                  Confirm
                </StandardButton>
              </StandardModal.Footer>
            </StandardModal>
          </StandardCard.Body>
        </StandardCard>

        {/* Data Table Demo */}
        <StandardCard>
          <StandardCard.Header title="Data Table" subtitle="With sorting and selection" />
          <StandardCard.Body>
            <StandardDataTable
              columns={tableColumns}
              data={demoData}
              selectable
              onRowClick={(row) => info('Row Clicked', `You clicked on ${row.name}`)}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Form Inputs Demo */}
        <StandardCard>
          <StandardCard.Header title="Form Inputs" />
          <StandardCard.Body className="space-y-4">
            <StandardInput
              label="Text Input"
              placeholder="Enter text..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              helpText="This is help text"
            />

            <StandardInput
              label="With Icon"
              placeholder="Email address"
              type="email"
              leftIcon={<User size={16} />}
            />

            <StandardInput
              label="With Error"
              value="invalid"
              onChange={() => {}}
              error="This field has an error"
            />

            <StandardSelect
              label="Select Dropdown"
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
              placeholder="Choose an option"
              options={[
                { value: 'option1', label: 'Option 1' },
                { value: 'option2', label: 'Option 2' },
                { value: 'option3', label: 'Option 3' }
              ]}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Checkbox Demo */}
        <StandardCard>
          <StandardCard.Header title="Checkbox" subtitle="With label, help text, and error states" />
          <StandardCard.Body className="space-y-4">
            <StandardCheckbox
              label="Basic Checkbox"
              checked={checkboxChecked}
              onChange={setCheckboxChecked}
            />

            <StandardCheckbox
              label="Checkbox with Help Text"
              checked={checkboxChecked}
              onChange={setCheckboxChecked}
              helpText="This provides additional context for the option"
            />

            <StandardCheckbox
              label="Checkbox with Error"
              checked={checkboxWithError}
              onChange={setCheckboxWithError}
              error="You must accept the terms and conditions"
            />

            <StandardCheckbox
              label="Disabled Checkbox"
              checked={true}
              onChange={() => {}}
              disabled
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Textarea Demo */}
        <StandardCard>
          <StandardCard.Header title="Textarea" subtitle="With character count, resize options, and validation" />
          <StandardCard.Body className="space-y-4">
            <StandardTextarea
              label="Basic Textarea"
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              placeholder="Enter your message..."
              helpText="Write your message here"
            />

            <StandardTextarea
              label="With Character Count"
              value={textareaWithCount}
              onChange={(e) => setTextareaWithCount(e.target.value)}
              placeholder="Limited to 200 characters..."
              maxLength={200}
              showCharCount
              rows={3}
            />

            <StandardTextarea
              label="With Error"
              value="Invalid content"
              onChange={() => {}}
              error="This field contains invalid content"
              rows={2}
            />

            <StandardTextarea
              label="No Resize"
              value=""
              onChange={() => {}}
              placeholder="This textarea cannot be resized"
              resize="none"
              rows={2}
            />

            <StandardTextarea
              label="Required Field"
              value=""
              onChange={() => {}}
              placeholder="This field is required"
              required
              rows={2}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Search Bar Demo */}
        <StandardCard>
          <StandardCard.Header title="Search Bar" subtitle="With debounce (300ms)" />
          <StandardCard.Body>
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search records..."
              onSearch={(value) => info('Search', `Searching for: ${value}`)}
            />
            {searchQuery && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Search query: {searchQuery}
              </p>
            )}
          </StandardCard.Body>
        </StandardCard>

        {/* Pagination Demo */}
        <StandardCard>
          <StandardCard.Header title="Pagination" />
          <StandardCard.Body>
            <StandardPagination
              currentPage={currentPage}
              totalPages={10}
              totalItems={100}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Loading States Demo */}
        <StandardCard>
          <StandardCard.Header title="Loading States" subtitle="4 variants" />
          <StandardCard.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded p-4">
                <h5 className="text-sm font-medium mb-2">Spinner</h5>
                <StandardLoadingState variant="spinner" message="Loading..." />
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded p-4">
                <h5 className="text-sm font-medium mb-2">Dots</h5>
                <StandardLoadingState variant="dots" message="Processing..." />
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded p-4">
                <h5 className="text-sm font-medium mb-2">Pulse</h5>
                <StandardLoadingState variant="pulse" />
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded p-4">
                <h5 className="text-sm font-medium mb-2">Skeleton</h5>
                <StandardLoadingState variant="skeleton" />
              </div>
            </div>
          </StandardCard.Body>
        </StandardCard>

        {/* Error State Demo */}
        <StandardCard>
          <StandardCard.Header title="Error State" />
          <StandardCard.Body>
            <StandardErrorState
              title="Failed to load data"
              message="An error occurred while loading the data."
              onRetry={() => success('Retry', 'Retrying...')}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Empty State Demo */}
        <StandardCard>
          <StandardCard.Header title="Empty State" />
          <StandardCard.Body>
            <StandardEmptyState
              icon={<Inbox size={48} />}
              title="No records found"
              message="Get started by creating your first record."
              actionLabel="Create Record"
              onAction={() => success('Create', 'Opening create form...')}
            />
          </StandardCard.Body>
        </StandardCard>

        {/* Toast Notifications Demo */}
        <StandardCard>
          <StandardCard.Header title="Toast Notifications" subtitle="4 types with auto-dismiss" />
          <StandardCard.Body>
            <div className="flex flex-wrap gap-2">
              <StandardButton
                variant="success"
                onClick={() => success('Success!', 'Operation completed successfully')}
              >
                Success Toast
              </StandardButton>
              <StandardButton
                variant="danger"
                onClick={() => error('Error!', 'Something went wrong')}
              >
                Error Toast
              </StandardButton>
              <StandardButton
                variant="warning"
                onClick={() => warning('Warning!', 'Please check your inputs')}
              >
                Warning Toast
              </StandardButton>
              <StandardButton
                variant="secondary"
                onClick={() => info('Info', 'Here is some information')}
              >
                Info Toast
              </StandardButton>
            </div>
          </StandardCard.Body>
        </StandardCard>
      </div>
    </div>
  );
};

export default ComponentLibraryDemo;
